import { useEffect, useState } from "react";
import { UserPlus, Users, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import api, { formatApiError } from "../../lib/api";

export default function AdminPlanners() {
  const [planners, setPlanners] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [deact, setDeact] = useState(null);
  const [reassign, setReassign] = useState("none");

  const load = () => api.get("/admin/planners").then((r) => setPlanners(r.data)).catch(() => setPlanners([]));
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; load is stable
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/planners", form);
      toast.success(`Planner account created for ${form.name}`);
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const deactivate = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/planners/${deact.id}`, { active: false, reassign_to: reassign === "none" ? null : reassign });
      toast.success(`${deact.name} deactivated — ${data.reassigned_cases} open case(s) ${reassign === "none" ? "unassigned" : "reassigned"}`);
      setDeact(null);
      setReassign("none");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const reactivate = async (p) => {
    try {
      await api.patch(`/admin/planners/${p.id}`, { active: true });
      toast.success(`${p.name} reactivated`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const otherActive = (planners || []).filter((p) => p.active !== false && p.id !== deact?.id);

  return (
    <div data-testid="admin-planners-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight">Planning Team</h1>
          <p className="text-sm text-slate-500 mt-1">Planner logins with personal case queues and workload views.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-planner-btn" className="rounded-full bg-sky-600 hover:bg-sky-700 font-bold">
              <UserPlus className="h-4 w-4 mr-2" /> Add Planner
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-planner-dialog">
            <DialogHeader><DialogTitle className="font-display font-bold text-xl">Create a planner account</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="add-planner-form">
              <div className="space-y-2">
                <Label htmlFor="p-name">Full name</Label>
                <Input id="p-name" data-testid="planner-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ana Ruiz" className="h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-email">Email</Label>
                <Input id="p-email" data-testid="planner-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="planner@orthoplanix.com" className="h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-pass">Password</Label>
                <Input id="p-pass" data-testid="planner-password-input" type="password" required minLength={8} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 8 characters" className="h-11" />
              </div>
              <Button type="submit" disabled={saving} data-testid="planner-create-btn" className="w-full rounded-full bg-sky-600 hover:bg-sky-700 font-bold">
                {saving ? "Creating…" : "Create planner account"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {planners === null ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" /></div>
      ) : planners.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-20 text-center" data-testid="planners-empty-state">
          <Users className="h-10 w-10 text-slate-300 mx-auto mb-4" />
          <p className="font-display font-bold text-xl">No planners yet</p>
          <p className="text-sm text-slate-500 mt-2">Create planner logins so cases can be assigned to real team members.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="planners-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 font-semibold">Planner</th>
                  <th className="px-5 py-3.5 font-semibold">Email</th>
                  <th className="px-5 py-3.5 font-semibold">Active cases</th>
                  <th className="px-5 py-3.5 font-semibold">Total cases</th>
                  <th className="px-5 py-3.5 font-semibold">Avg turnaround</th>
                  <th className="px-5 py-3.5 font-semibold">Status</th>
                  <th className="px-5 py-3.5 font-semibold">Joined</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {planners.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors" data-testid={`planner-row-${p.email}`}>
                    <td className="px-5 py-4 font-semibold">{p.name}</td>
                    <td className="px-5 py-4 text-slate-500">{p.email}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${p.active_cases > 0 ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                        {p.active_cases} active
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-500">{p.total_cases}</td>
                    <td className="px-5 py-4" data-testid={`planner-tat-${p.email}`}>
                      {p.avg_turnaround_days != null ? (
                        <span className="font-bold text-slate-700">{p.avg_turnaround_days}d <span className="font-normal text-xs text-slate-400">/ {p.delivered_cases} delivered</span></span>
                      ) : (
                        <span className="text-slate-400 text-xs">No deliveries yet</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${p.active !== false ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-400 border border-slate-200"}`} data-testid={`planner-status-${p.email}`}>
                        {p.active !== false ? "Active" : "Deactivated"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-500 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right">
                      {p.active !== false ? (
                        <button onClick={() => { setDeact(p); setReassign("none"); }} data-testid={`planner-deactivate-${p.email}`}
                          className="inline-flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700">
                          <UserX className="h-3.5 w-3.5" /> Deactivate
                        </button>
                      ) : (
                        <button onClick={() => reactivate(p)} data-testid={`planner-reactivate-${p.email}`}
                          className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-800">
                          <UserCheck className="h-3.5 w-3.5" /> Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!deact} onOpenChange={(o) => { if (!o) setDeact(null); }}>
        <DialogContent data-testid="deactivate-planner-dialog">
          <DialogHeader><DialogTitle className="font-display font-bold text-xl">Deactivate {deact?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">
            They will no longer be able to sign in. Their <strong>{deact?.active_cases} open case(s)</strong> can be handed to another planner or left unassigned.
          </p>
          <Select value={reassign} onValueChange={setReassign}>
            <SelectTrigger className="h-11" data-testid="reassign-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Leave cases unassigned</SelectItem>
              {otherActive.map((p) => <SelectItem key={p.id} value={p.id}>Reassign to {p.name} ({p.active_cases} active)</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={deactivate} disabled={saving} data-testid="deactivate-confirm-btn"
            className="w-full rounded-full bg-red-600 hover:bg-red-700 font-bold">
            {saving ? "Deactivating…" : "Deactivate planner"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
